import { kebabCase } from 'lodash';
import { pascalCase } from 'utils/pascal-case';
import { unreachable } from 'utils/unreachable';
import { MultiLineTextField, TextField } from '../fields';
import { TermSeparator } from './separators';

export enum TextFieldFilterOperationType {
  Contains = 'Contains',
  DoesNotContain = 'DoesNotContain',
  IsEmpty = 'IsEmpty',
  IsNotEmpty = 'IsNotEmpty',
}

export const TextFieldFilterOperationTypes = [
  TextFieldFilterOperationType.Contains,
  TextFieldFilterOperationType.DoesNotContain,
  TextFieldFilterOperationType.IsEmpty,
  TextFieldFilterOperationType.IsNotEmpty,
];

export const isOptional = (type: TextFieldFilterOperationType) =>
  type === TextFieldFilterOperationType.IsEmpty ||
  type === TextFieldFilterOperationType.IsNotEmpty;

interface TextFieldFilterBase {
  field: TextField | MultiLineTextField;
}

interface TextFieldValueFilterBase extends TextFieldFilterBase {
  value?: string;
}

interface TextContainsFilter extends TextFieldValueFilterBase {
  type: TextFieldFilterOperationType.Contains;
}
interface TextDoesNotContainFilter extends TextFieldValueFilterBase {
  type: TextFieldFilterOperationType.DoesNotContain;
}

interface TextIsEmptyFilter extends TextFieldFilterBase {
  type: TextFieldFilterOperationType.IsEmpty;
}

interface TextIsNotEmptyFilter extends TextFieldFilterBase {
  type: TextFieldFilterOperationType.IsNotEmpty;
}

export type TextFieldFilter =
  | TextContainsFilter
  | TextDoesNotContainFilter
  | TextIsEmptyFilter
  | TextIsNotEmptyFilter;

const isValid = (filter: TextFieldFilter) => {
  switch (filter.type) {
    case TextFieldFilterOperationType.Contains:
    case TextFieldFilterOperationType.DoesNotContain:
      return filter.value != null && filter.value.trim().length > 0;
    default:
      return true;
  }
};

const toString = (filter: TextFieldFilter): string => {
  const type = kebabCase(filter.type);

  switch (filter.type) {
    case TextFieldFilterOperationType.IsEmpty:
    case TextFieldFilterOperationType.IsNotEmpty:
      return type;
    case TextFieldFilterOperationType.Contains:
    case TextFieldFilterOperationType.DoesNotContain:
      return [type, filter.value == null ? 'null' : filter.value].join(
        TermSeparator,
      );
    default:
      return unreachable(filter);
  }
};

const parse = (value: string, field: TextField): TextFieldFilter => {
  const [type_string, meta] = value.split(TermSeparator);
  const type = pascalCase(type_string) as TextFieldFilterOperationType;

  switch (type) {
    case TextFieldFilterOperationType.IsEmpty:
    case TextFieldFilterOperationType.IsNotEmpty:
      return { field, type };
    case TextFieldFilterOperationType.Contains:
    case TextFieldFilterOperationType.DoesNotContain: {
      const string = JSON.parse(`"${meta}"`) as string | null;

      return { field, type, value: string == null ? undefined : string };
    }
    default:
      return unreachable(type);
  }
};

export const TextFieldFilter = {
  isValid,
  toString,
  parse,
};
